import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Tooltip,
} from '@mui/material';
import MainCard from '../berry/ui-component/cards/MainCard.jsx';
import UserContext from '../context/user.jsx';
import api_json from '../api/json.js';
import countries from '../countries.js';
import FileSelector from '../components/file_selector/component.jsx';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';

const MAP_WIDTH = 1920;
const MAP_HEIGHT = 1080;
const PIN_SIZE = 100;
const OBJECT_SIZE = 50;

function get_default_data() {
	return {
		pins: [],
		objects: [],
	};
}

function clamp_position(x, y, size) {
	return {
		x: Math.max(0, Math.min(MAP_WIDTH - size, x)),
		y: Math.max(0, Math.min(MAP_HEIGHT - size, y)),
	};
}

function get_rects(data) {
	const rects = [];
	data.pins.forEach((pin, index) => {
		rects.push({
			kind: 'pin',
			index,
			x: pin.x || 0,
			y: pin.y || 0,
			size: PIN_SIZE,
		});
	});
	data.objects.forEach((object, index) => {
		rects.push({
			kind: 'object',
			index,
			x: object.x || 0,
			y: object.y || 0,
			size: OBJECT_SIZE,
		});
	});
	return rects;
}

function cycle_same_rect(selected, data) {
	if (!selected) return selected;
	const rects = get_rects(data);
	const current = rects.find(
		(r) => r.kind === selected.kind && r.index === selected.index
	);
	if (!current) return selected;
	const same_rect = rects.filter(
		(r) =>
			r.x === current.x &&
			r.y === current.y &&
			r.size === current.size
	);
	if (same_rect.length < 2) return selected;
	const position = same_rect.findIndex(
		(r) => r.kind === selected.kind && r.index === selected.index
	);
	const next = same_rect[(position + 1) % same_rect.length];
	return { kind: next.kind, index: next.index };
}

function strip_html_for_preview(str) {
	return String(str || '')
		.replace(/<[^>]*>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function pin_english_content_preview(pin) {
	const en = pin.content?.en;
	const years = String(en?.years ?? '').trim();
	const title = String(en?.title ?? '').trim();
	const desc = strip_html_for_preview(en?.desc);
	if (!years && !title && !desc) return '';
	return `${years} | ${title} | ${desc}`;
}

function CAF() {
	const { session } = useContext(UserContext);
	const [data, set_data] = useState(get_default_data);
	const [loading, set_loading] = useState(true);
	const [saving, set_saving] = useState(false);
	const [selected, set_selected] = useState(null);
	const [drag, set_drag] = useState(null);

	useEffect(() => {
		if (!session) return;
		set_loading(true);
		api_json
			.get({ session, app_uid: 'amhc' })
			.then((json) => {
				const next = json || {};
				if (!Array.isArray(next.pins)) next.pins = [];
				if (!Array.isArray(next.objects)) next.objects = [];
				set_data({
					pins: next.pins.map((pin) => ({
						x: Number.isFinite(pin.x) ? pin.x : 0,
						y: Number.isFinite(pin.y) ? pin.y : 0,
						country: pin.country || (countries[0] ? countries[0].uid : ''),
						content: pin.content || {
							en: { years: '', title: '', desc: '', qr: '' },
							fr: { years: '', title: '', desc: '', qr: '' },
							gallery: [],
						},
					})),
					objects: next.objects.map((object) => ({
						x: Number.isFinite(object.x) ? object.x : 0,
						y: Number.isFinite(object.y) ? object.y : 0,
						country: object.country || 'any',
						type: object.type || 'black_1',
					})),
				});
			})
			.catch(console.error)
			.finally(() => set_loading(false));
	}, [session]);

	const selected_item = useMemo(() => {
		if (!selected) return null;
		if (selected.kind === 'pin') return data.pins[selected.index] || null;
		if (selected.kind === 'object') return data.objects[selected.index] || null;
		return null;
	}, [data, selected]);

	const handle_add_pin = () => {
		const next = {
			x: (MAP_WIDTH - PIN_SIZE) / 2,
			y: (MAP_HEIGHT - PIN_SIZE) / 2,
			country: 'canada',
			content: {
				en: { years: '', title: '', desc: '', qr: '' },
				fr: { years: '', title: '', desc: '', qr: '' },
				gallery: [],
			},
		};
		set_data((prev) => {
			const pins = [...prev.pins, next];
			return { ...prev, pins };
		});
		set_selected({ kind: 'pin', index: data.pins.length });
	};

	const handle_add_object = () => {
		const next = {
			x: (MAP_WIDTH - OBJECT_SIZE) / 2,
			y: (MAP_HEIGHT - OBJECT_SIZE) / 2,
			country: 'any',
			type: 'black_1',
		};
		set_data((prev) => {
			const objects = [...prev.objects, next];
			return { ...prev, objects };
		});
		set_selected({ kind: 'object', index: data.objects.length });
	};

	const handle_select = (kind, index) => {
		if (
			selected &&
			selected.kind === kind &&
			selected.index === index
		) {
			set_selected((prev) => cycle_same_rect(prev, data));
			return;
		}
		set_selected({ kind, index });
	};

	const handle_delete_selected = () => {
		if (!selected) return;
		if (selected.kind === 'pin') {
			set_data((prev) => {
				const pins = prev.pins.filter((_, i) => i !== selected.index);
				return { ...prev, pins };
			});
		} else if (selected.kind === 'object') {
			set_data((prev) => {
				const objects = prev.objects.filter((_, i) => i !== selected.index);
				return { ...prev, objects };
			});
		}
		set_selected(null);
	};

	const handle_mouse_down = (e, kind, index) => {
		const rect = e.currentTarget.getBoundingClientRect();
		const item =
			kind === 'pin' ? data.pins[index] : data.objects[index];
		if (!item) return;
		const offset_x = e.clientX - rect.left;
		const offset_y = e.clientY - rect.top;
		set_drag({
			kind,
			index,
			offset_x,
			offset_y,
		});
		handle_select(kind, index);
	};

	const handle_mouse_move = (e) => {
		if (!drag) return;
		const map_rect = e.currentTarget.getBoundingClientRect();
		const x = e.clientX - map_rect.left - drag.offset_x;
		const y = e.clientY - map_rect.top - drag.offset_y;
		const size = drag.kind === 'pin' ? PIN_SIZE : OBJECT_SIZE;
		const clamped = clamp_position(x, y, size);
		set_data((prev) => {
			if (drag.kind === 'pin') {
				const pins = prev.pins.slice();
				if (!pins[drag.index]) return prev;
				pins[drag.index] = {
					...pins[drag.index],
					x: clamped.x,
					y: clamped.y,
				};
				return { ...prev, pins };
			}
			const objects = prev.objects.slice();
			if (!objects[drag.index]) return prev;
			objects[drag.index] = {
				...objects[drag.index],
				x: clamped.x,
				y: clamped.y,
			};
			return { ...prev, objects };
		});
	};

	const handle_mouse_up = () => {
		if (drag) set_drag(null);
	};

	const handle_change_field = (path, value) => {
		set_data((prev) => {
			const next = { ...prev };
			let target = next;
			for (let i = 0; i < path.length - 1; i += 1) {
				const key = path[i];
				const next_key = path[i + 1];

				if (Array.isArray(target)) {
					// target is an array; ensure element exists
					if (target[key] == null) {
						target[key] = typeof next_key === 'number' ? [] : {};
					}
					target = target[key];
				} else {
					if (target[key] == null) {
						target[key] = typeof next_key === 'number' ? [] : {};
					} else if (Array.isArray(target[key])) {
						target[key] = [...target[key]];
					} else if (typeof target[key] === 'object') {
						target[key] = { ...target[key] };
					}
					target = target[key];
				}
			}
			const last = path[path.length - 1];
			if (Array.isArray(target)) {
				target[last] = value;
			} else {
				target[last] = value;
			}
			return next;
		});
	};

	const handle_add_gallery_item = () => {
		if (!selected || selected.kind !== 'pin') return;
		handle_change_field(
			[
				'pins',
				selected.index,
				'content',
				'gallery',
			],
			[
				...(data.pins[selected.index]?.content?.gallery || []),
				{ type: 'img', src: '' },
			]
		);
	};

	const handle_delete_gallery_item = (gallery_index) => {
		if (!selected || selected.kind !== 'pin') return;
		const pin = data.pins[selected.index];
		const gallery = pin?.content?.gallery || [];
		handle_change_field(
			[
				'pins',
				selected.index,
				'content',
				'gallery',
			],
			gallery.filter((_, i) => i !== gallery_index)
		);
	};

	const handle_move_gallery_item = (from_index, to_index) => {
		if (!selected || selected.kind !== 'pin') return;
		if (to_index < 0) return;
		const pin = data.pins[selected.index];
		const gallery = pin?.content?.gallery || [];
		if (to_index >= gallery.length) return;
		const next_gallery = gallery.slice();
		const [moved] = next_gallery.splice(from_index, 1);
		next_gallery.splice(to_index, 0, moved);
		handle_change_field(
			[
				'pins',
				selected.index,
				'content',
				'gallery',
			],
			next_gallery
		);
	};

	const handle_save = () => {
		if (!session) return;
		set_saving(true);
		api_json
			.save({
				session,
				app_uid: 'amhc',
				data: {
					pins: data.pins,
					objects: data.objects,
				},
			})
			.catch(console.error)
			.finally(() => set_saving(false));
	};

	if (!session || loading) {
		return (
			<CircularProgress size={100} sx={{ mx: 'auto', mt: 20, display: 'block' }} />
		);
	}

	const selected_gallery =
		selected &&
		selected.kind === 'pin' &&
		data.pins[selected.index]?.content?.gallery
			? data.pins[selected.index].content.gallery
			: [];

	return (
		<>
			<MainCard title="AMHC Map Configuration" sx={{ mb: 3 }}>
				<Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
					<Button variant="contained" onClick={handle_add_pin}>
						Add pin
					</Button>
					<Button variant="contained" onClick={handle_add_object}>
						Add object
					</Button>
					<Button
						variant="outlined"
						color="error"
						onClick={handle_delete_selected}
						disabled={!selected}
					>
						Delete selected
					</Button>
					<Box sx={{ flexGrow: 1 }} />
					<Button
						variant="contained"
						color="primary"
						onClick={handle_save}
						disabled={saving}
					>
						{saving ? 'Saving...' : 'Save'}
					</Button>
				</Box>
				<Box
					sx={{
						border: '1px solid #ccc',
						overflow: 'auto',
						maxWidth: '100%',
					}}
				>
					<Box
						onMouseMove={handle_mouse_move}
						onMouseUp={handle_mouse_up}
						onMouseLeave={handle_mouse_up}
						sx={{
							position: 'relative',
							width: MAP_WIDTH,
							height: MAP_HEIGHT,
							background: 'url(/img/bg.jpg) no-repeat center center',
							backgroundSize: 'cover',
						}}
					>
						{data.pins.map((pin, index) => (
							<Box
								key={`pin_${index}`}
								onMouseDown={(e) => handle_mouse_down(e, 'pin', index)}
								onClick={() => handle_select('pin', index)}
								sx={{
									position: 'absolute',
									left: pin.x,
									top: pin.y,
									width: PIN_SIZE,
									height: PIN_SIZE,
									border:
										selected &&
										selected.kind === 'pin' &&
										selected.index === index
											? '2px solid #ff0'
											: '1px solid transparent',
									borderRadius: '50%',
									backgroundRepeat: 'no-repeat',
									backgroundPosition: 'center',
									backgroundSize: 'contain',
									backgroundImage: pin.country && pin.country !== 'any'
										? `url(/img/pins/${pin.country}.png)`
										: 'none',
									cursor: 'move',
									boxSizing: 'border-box',
								}}
							/>
						))}
						{data.objects.map((object, index) => (
							<Box
								key={`object_${index}`}
								onMouseDown={(e) => handle_mouse_down(e, 'object', index)}
								onClick={() => handle_select('object', index)}
								sx={{
									position: 'absolute',
									left: object.x,
									top: object.y,
									width: OBJECT_SIZE,
									height: OBJECT_SIZE,
									border:
										selected &&
										selected.kind === 'object' &&
										selected.index === index
											? '2px solid #0f0'
											: '1px solid transparent',
									borderRadius: '4px',
									backgroundRepeat: 'no-repeat',
									backgroundPosition: 'center',
									backgroundSize: 'contain',
									backgroundImage: object.type
										? `url(/img/pins/icon_${object.type}.png)`
										: 'none',
									cursor: 'move',
									boxSizing: 'border-box',
								}}
							/>
						))}
					</Box>
				</Box>
				<Box sx={{ mt: 2 }}>
					<TableContainer sx={{ mb: 2 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Type</TableCell>
									<TableCell>Preview</TableCell>
									<TableCell>X</TableCell>
									<TableCell>Y</TableCell>
									<TableCell>Country</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.pins.map((pin, index) => {
									const pin_preview = pin_english_content_preview(pin);
									return (
									<TableRow
										key={`pin_row_${index}`}
										hover
										selected={
											selected &&
											selected.kind === 'pin' &&
											selected.index === index
										}
										onClick={() => handle_select('pin', index)}
									>
										<TableCell>Pin</TableCell>
										<TableCell
											sx={{
												maxWidth: 360,
												overflow: 'hidden',
												whiteSpace: 'nowrap',
												textOverflow: 'ellipsis',
											}}
										>
											<Tooltip
												title={pin_preview || '(empty)'}
												placement="top-start"
												enterDelay={1000}
											>
												<Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
													{pin_preview || '—'}
												</Box>
											</Tooltip>
										</TableCell>
										<TableCell>{pin.x}</TableCell>
										<TableCell>{pin.y}</TableCell>
										<TableCell>{pin.country}</TableCell>
									</TableRow>
									);
								})}
								{data.objects.map((object, index) => (
									<TableRow
										key={`object_row_${index}`}
										hover
										selected={
											selected &&
											selected.kind === 'object' &&
											selected.index === index
										}
										onClick={() => handle_select('object', index)}
									>
										<TableCell>Object</TableCell>
										<TableCell>
											<Box
												sx={{
													width: 32,
													height: 32,
													borderRadius: 1,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													backgroundColor: object.type && object.type.startsWith('white_') ? '#000' : 'transparent',
												}}
											>
												<Box
													sx={{
														width: 24,
														height: 24,
														backgroundRepeat: 'no-repeat',
														backgroundPosition: 'center',
														backgroundSize: 'contain',
														backgroundImage: object.type
															? `url(/img/pins/icon_${object.type}.png)`
															: 'none',
													}}
												/>
											</Box>
										</TableCell>
										<TableCell>{object.x}</TableCell>
										<TableCell>{object.y}</TableCell>
										<TableCell>{object.country}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
						<Box>
							<Typography variant="h6" sx={{ mb: 1 }}>
								Selected item
							</Typography>
							{!selected_item ? (
								<Typography color="textSecondary">
									Select a pin or object on the map or in the list.
								</Typography>
							) : (
								<Box sx={{ display: 'flex', flexWrap: selected.kind === 'object' ? 'nowrap' : 'wrap', gap: 2 }}>
									<TextField
										label="X"
										type="number"
										size="small"
										value={selected_item.x}
										onChange={(e) =>
											handle_change_field(
												[
													selected.kind === 'pin' ? 'pins' : 'objects',
													selected.index,
													'x',
												],
												Number(e.target.value) || 0
											)
										}
										sx={{ width: 120 }}
									/>
									<TextField
										label="Y"
										type="number"
										size="small"
										value={selected_item.y}
										onChange={(e) =>
											handle_change_field(
												[
													selected.kind === 'pin' ? 'pins' : 'objects',
													selected.index,
													'y',
												],
												Number(e.target.value) || 0
											)
										}
										sx={{ width: 120 }}
									/>
									<FormControl size="small" sx={{ minWidth: 160 }}>
										<InputLabel id="country-label">Country</InputLabel>
										<Select
											labelId="country-label"
											label="Country"
											value={
												selected.kind === 'pin'
													? selected_item.country || (countries[0] ? countries[0].uid : '')
													: selected_item.country || 'any'
											}
											onChange={(e) =>
												handle_change_field(
													[
														selected.kind === 'pin' ? 'pins' : 'objects',
														selected.index,
														'country',
													],
													e.target.value
												)
											}
										>
											{selected.kind === 'object' && (
												<MenuItem value="any">Any</MenuItem>
											)}
											{countries.map((country) => (
												<MenuItem key={country.uid} value={country.uid}>
													{country.name_en}
												</MenuItem>
											))}
										</Select>
									</FormControl>
									{selected.kind === 'object' && (
										<FormControl size="small" sx={{ minWidth: 160 }}>
											<InputLabel id="type-label">Type</InputLabel>
											<Select
												labelId="type-label"
												label="Type"
												value={selected_item.type || 'black_1'}
												onChange={(e) =>
													handle_change_field(
														['objects', selected.index, 'type'],
														e.target.value
													)
												}
												MenuProps={{
													PaperProps: {
														sx: { columns: 2 },
													},
												}}
											>
												{Array.from({ length: 10 }).map((_, i) => {
													const value = `black_${i + 1}`;
													return (
														<MenuItem
															key={value}
															value={value}
														>
															<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
																<Box
																	sx={{
																		width: 32,
																		height: 32,
																		backgroundRepeat: 'no-repeat',
																		backgroundPosition: 'center',
																		backgroundSize: 'contain',
																		backgroundImage: `url(/img/pins/icon_${value}.png)`,
																	}}
																/>
																{value}
															</Box>
														</MenuItem>
													);
												})}
												{Array.from({ length: 10 }).map((_, i) => {
													const value = `white_${i + 1}`;
													return (
														<MenuItem
															key={value}
															value={value}
														>
															<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
																<Box
																	sx={{
																		width: 32,
																		height: 32,
																		backgroundColor: '#000',
																		borderRadius: 1,
																		display: 'flex',
																		alignItems: 'center',
																		justifyContent: 'center',
																		backgroundRepeat: 'no-repeat',
																		backgroundPosition: 'center',
																		backgroundSize: 'contain',
																		backgroundImage: `url(/img/pins/icon_${value}.png)`,
																	}}
																></Box>
																{value}
															</Box>
														</MenuItem>
													);
												})}
											</Select>
										</FormControl>
									)}
								</Box>
							)}
						</Box>
						{selected &&
							selected.kind === 'pin' &&
							selected_item && (
								<Box sx={{ flexGrow: 1, minWidth: 320 }}>
									<Typography variant="h6" sx={{ mb: 1 }}>
										Pin content
									</Typography>
									<Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
										{['en', 'fr'].map((lang) => (
											<Box key={lang} sx={{ minWidth: 260, flex: 1 }}>
												<Typography variant="subtitle1" sx={{ mb: 1 }}>
													{lang.toUpperCase()}
												</Typography>
												<TextField
													label="Years"
													size="small"
													fullWidth
													value={
														selected_item.content?.[lang]?.years || ''
													}
													onChange={(e) =>
														handle_change_field(
															[
																'pins',
																selected.index,
																'content',
																lang,
																'years',
															],
															e.target.value
														)
													}
													sx={{ mb: 1 }}
												/>
												<TextField
													label="Title"
													size="small"
													fullWidth
													value={
														selected_item.content?.[lang]?.title || ''
													}
													onChange={(e) =>
														handle_change_field(
															[
																'pins',
																selected.index,
																'content',
																lang,
																'title',
															],
															e.target.value
														)
													}
													sx={{ mb: 1 }}
												/>
												<TextField
													label="Description (HTML allowed)"
													size="small"
													fullWidth
													multiline
													minRows={4}
													value={
														selected_item.content?.[lang]?.desc || ''
													}
													onChange={(e) =>
														handle_change_field(
															[
																'pins',
																selected.index,
																'content',
																lang,
																'desc',
															],
															e.target.value
														)
													}
													sx={{ mb: 1 }}
												/>
												<Box sx={{ mt: 1 }}>
													<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
														QR file
													</Typography>
													<FileSelector
														type="image"
														value={selected_item.content?.[lang]?.qr || ''}
														on_change={(name) =>
															handle_change_field(
																[
																	'pins',
																	selected.index,
																	'content',
																	lang,
																	'qr',
																],
																name || ''
															)
														}
													/>
												</Box>
											</Box>
										))}
									</Box>
									<Box sx={{ mt: 3 }}>
										<Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
											<Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
												Gallery
											</Typography>
											<Button
												variant="outlined"
												size="small"
												onClick={handle_add_gallery_item}
											>
												Add item
											</Button>
										</Box>
										{!selected_gallery.length ? (
											<Typography color="textSecondary">
												No gallery items.
											</Typography>
										) : (
											<TableContainer>
												<Table size="small">
													<TableHead>
														<TableRow>
															<TableCell>Type</TableCell>
															<TableCell>Source file</TableCell>
															<TableCell align="right">Actions</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{selected_gallery.map(
															(item, gallery_index) => (
																<TableRow
																	key={gallery_index}
																	hover
																>
																	<TableCell>
																		<FormControl
																			size="small"
																			fullWidth
																		>
																			<Select
																				value={item.type || 'img'}
																				onChange={(e) =>
																					handle_change_field(
																						[
																							'pins',
																							selected.index,
																							'content',
																							'gallery',
																							gallery_index,
																							'type',
																						],
																						e.target.value
																					)
																				}
																			>
																				<MenuItem value="img">
																					Image
																				</MenuItem>
																				<MenuItem value="video">
																					Video
																				</MenuItem>
																			</Select>
																		</FormControl>
																	</TableCell>
																	<TableCell>
																		<Box
																			sx={{
																				display: 'flex',
																				gap: 0.5,
																				alignItems: 'center',
																			}}
																		>
																			<FileSelector
																				type={item.type === 'video' ? 'video' : 'image'}
																				value={item.src || ''}
																				on_change={(name) => {
																					const n = name || '';
																					const lower = n.toLowerCase();
																					const is_video = /\.(mp4|webm|mov|m4v|avi|mkv)$/.test(lower);
																					const t = is_video ? 'video' : 'img';
																					handle_change_field(
																						[
																							'pins',
																							selected.index,
																							'content',
																							'gallery',
																							gallery_index,
																							'type',
																						],
																						t
																					);
																					handle_change_field(
																						[
																							'pins',
																							selected.index,
																							'content',
																							'gallery',
																							gallery_index,
																							'src',
																						],
																						n
																					);
																				}}
																			/>
																		</Box>
																	</TableCell>
																	<TableCell align="right">
																		{gallery_index > 0 && (
																			<Tooltip title="Move up">
																				<IconButton
																					size="small"
																					onClick={() =>
																						handle_move_gallery_item(
																							gallery_index,
																							gallery_index - 1
																						)
																					}
																				>
																					<ArrowUpwardIcon fontSize="inherit" />
																				</IconButton>
																			</Tooltip>
																		)}
																		{gallery_index < selected_gallery.length - 1 && (
																			<Tooltip title="Move down">
																				<IconButton
																					size="small"
																					onClick={() =>
																						handle_move_gallery_item(
																							gallery_index,
																							gallery_index + 1
																						)
																					}
																				>
																					<ArrowDownwardIcon fontSize="inherit" />
																				</IconButton>
																			</Tooltip>
																		)}
																		<Tooltip title="Delete item">
																			<IconButton
																				size="small"
																				color="error"
																				onClick={() =>
																					handle_delete_gallery_item(
																						gallery_index
																					)
																				}
																			>
																				<DeleteIcon fontSize="inherit" />
																			</IconButton>
																		</Tooltip>
																	</TableCell>
																</TableRow>
															)
														)}
													</TableBody>
												</Table>
											</TableContainer>
										)}
									</Box>
								</Box>
							)}
					</Box>
				</Box>
			</MainCard>
		</>
	);
}

export default CAF;

